import type { SensorType } from '../../../types';

/**
 * Displacement sheet "Sensor type" families (EDE-VXX-RC, …) were historically
 * not stored in the DB. Product models are (EDE-V05-RC, EDE-V015-SC, …).
 * Derive the family for UI grouping by replacing digits after EDE-V with XX.
 *
 * This is now a fallback only, used for sensor_type rows that don't have
 * `meta_data.family` configured yet — see getEffectiveModelFamily below.
 */
export const deriveDisplacementModelFamily = (
  model?: string | null
): string | null => {
  if (!model) return null;
  const trimmed = model.trim();
  if (!trimmed) return null;

  // EDE-V05-RC → EDE-VXX-RC ; EDE-V015-SC → EDE-VXX-SC ; EDE-V10-RC-WP → EDE-VXX-RC-WP
  if (/^EDE-V\d+/i.test(trimmed)) {
    return trimmed.replace(/^EDE-V\d+/i, "EDE-VXX");
  }

  return null;
};

export const isDisplacementCategory = (category?: string | null): boolean =>
  (category || "").trim().toLowerCase() === "displacement";

/**
 * Effective family for a sensor_type row: prefer the configured
 * `meta_data.family` from the DB, falling back to the regex-derived
 * family for rows that haven't been migrated to meta_data yet.
 */
export const getEffectiveModelFamily = (
  sensorType: Pick<SensorType, 'model' | 'meta_data'>
): string | null =>
  sensorType.meta_data?.family ?? deriveDisplacementModelFamily(sensorType.model);

export const rowBelongsToFamily = (
  sensorType: Pick<SensorType, 'model' | 'meta_data'>,
  family: string
): boolean => getEffectiveModelFamily(sensorType) === family;
